// whatsapp-poll-preview.tsx — THE poll bubble content, drawn the way WhatsApp
// draws a poll: bold question, a "Select one" / "Select one or more" line, and
// per option a radio (a rounded square when several answers are allowed), the
// text, its vote count and a bar under it.
//
// The one place a poll is rendered for display — the compose preview, the
// scheduled-messages list and the team inbox all use it. Pure: it takes the
// poll and touches no editor state. (The scheduled list used to render the
// poll EDITOR disabled, which seeded — and on unmount reset — the live editor.)
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import type { Poll } from '@extension/context/content/components/add-scheduled-message-button/schedule-message-types';

export default function WhatsAppPollPreview({ poll }: { poll?: Partial<Poll> | null }) {
    const { t } = useTranslation(namespace);
    if (!poll) return null;

    const options = (poll.options ?? []).filter((o) => o?.text);
    const multiple = !!poll.allowMultiple;

    return (
        // WhatsApp keeps a poll bubble wide even when the answers are short.
        <Box sx={{ minWidth: 'min(260px, 100%)', color: 'var(--whatsapp-text, #111b21)' }}>
            <Typography dir="auto" sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word' }}>
                {poll.question}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '4px', mb: '12px', fontSize: '13px', opacity: 0.6 }}>
                <CheckCircleIcon sx={{ fontSize: '14px' }} />
                {multiple
                    ? t('poll.selectOneOrMore', 'Select one or more')
                    : t('poll.selectOne', 'Select one')}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {options.map((option, index) => (
                    <Box key={option.id ?? index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Box sx={{
                                flex: 'none',
                                width: 20,
                                height: 20,
                                borderRadius: multiple ? '5px' : '50%',
                                border: '2px solid currentColor',
                                opacity: 0.45,
                            }} />
                            <Typography dir="auto" sx={{ flex: 1, minWidth: 0, fontSize: '15px', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                {option.text}
                            </Typography>
                            <Typography sx={{ flex: 'none', fontSize: '13px', opacity: 0.7 }}>0</Typography>
                        </Box>
                        {/* The vote bar: an empty track, lined up under the text. */}
                        <Box sx={{
                            mt: '8px',
                            marginInlineStart: '32px',
                            height: '6px',
                            borderRadius: '3px',
                            bgcolor: 'currentColor',
                            opacity: 0.1,
                        }} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
